/**
 * YawmiModule — daily-life scheduler for AWENI.
 *
 * v1 ships the calendar-grid door:
 *   - Month-view grid (7 cols × 5–6 rows)
 *   - Each cell shows Gregorian day + Hijri day + holiday badges
 *   - Click a day → expand events list below the grid
 *   - Prev/next month nav, today highlight, RTL support
 *   - Pulls from HijriCalendar (Islamic + national holidays catalog)
 *   - Synchronous render — no network, no awaits
 *
 * v2 (future):
 *   - Habit tracker reading cross-module activity events via SDK
 *   - Wishing-card composer + RDV scheduler + Atomic Habits engine
 *   - Voice reflection (Tier 3 dispatch prompt)
 *
 * Yawmi vs Memoriz boundary (NON-NEGOTIABLE per AWENI/CLAUDE.md):
 *   No Islamic content (verses, hadith, duas, Arabic religious text) lives
 *   inside Yawmi. The calendar shows Islamic HOLIDAY DATES (Eid, Ramadan,
 *   etc.) — that's calendar metadata, not Islamic content. If a user opens
 *   an Islamic holiday cell, Yawmi may eventually link out to Memoriz/Hisni
 *   for the relevant adhkar/dua, but never embed the religious text itself.
 */

import type {
  PreviewableAweniModule,
  PreviewSnapshot,
  MountedInstance,
  ModuleRoute,
} from '../types';
import type { AweniContext } from '../../types';
import { createHijriCalendar } from '../../services/HijriCalendar.impl';
import type {
  Country,
  HijriMethod,
  ResolvedEvent,
} from '../../services/HijriCalendar';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
import { getHesitationContext } from '../../services/HijriHesitation';
import { SourceAttributionStrip } from '../../shell/SourceAttributionStrip';
import { getEventsService } from '../../services/EventsService';
import type { CalendarEvent } from '../home/types';
import { HabitTab } from './HabitTab';
import { AlarmTab } from './AlarmTab';
import { ReminderTab } from './ReminderTab';
import { DuaJournalTab } from './DuaJournalTab';
import { HealthTab } from './HealthTab';
import { PeopleTab } from './PeopleTab';
import { HistoryTab } from './HistoryTab';

const ROUTES: ModuleRoute[] = [
  { path: '/yawmi', primary: true },
  { path: '/yawmi/calendar' },
  { path: '/yawmi/habits' },
  { path: '/yawmi/alarms' },
  { path: '/yawmi/reminders' },
];

let mountSeq = 0;
const SNAPSHOT_CACHE_KEY = 'aweni_preview_yawmi';
const STYLE_FLAG = '__aweni_yawmi_calendar_css__';

function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

function readUserCountry(): Country {
  try {
    // Phase 3d (2026-05-16) — Yawmi calendar now respects the unified
    // `aweni_calendar_prefs.countryCode` (same key as the deprecated
    // CalendarModal). Falls back to legacy `aweni_pref_country` then 'global'.
    const prefsRaw = localStorage.getItem(CAL_PREFS_LS_KEY);
    if (prefsRaw) {
      const p = JSON.parse(prefsRaw) as { countryCode?: string };
      if (p?.countryCode && /^[A-Z]{2}$/.test(p.countryCode)) return p.countryCode as Country;
    }
    const raw = localStorage.getItem('aweni_pref_country');
    if (raw && /^[A-Z]{2}$/.test(raw)) return raw as Country;
  } catch { /* ignore */ }
  return 'global';
}

// Phase 3d (2026-05-16) — Calendar prefs shared with the (deprecated) CalendarModal.
// Same localStorage key for migration-friendliness.
const CAL_PREFS_LS_KEY = 'aweni_calendar_prefs';

interface CalendarPrefs {
  /** 'gregorian' hides Hijri num; 'hijri' hides Gregorian num; 'both' shows both. */
  mode: 'gregorian' | 'both' | 'hijri';
  /** 0=Sun, 1=Mon, 6=Sat. */
  weekStart: 0 | 1 | 6;
  showHolidays: boolean;
  showIslamic: boolean;
  showPersonal: boolean;
  countryCode: string | null;
}

const DEFAULT_CAL_PREFS: CalendarPrefs = {
  mode: 'both',
  weekStart: 1,
  showHolidays: true,
  showIslamic: true,
  showPersonal: true,
  countryCode: null,
};

function loadCalPrefs(): CalendarPrefs {
  try {
    const raw = localStorage.getItem(CAL_PREFS_LS_KEY);
    if (!raw) return { ...DEFAULT_CAL_PREFS };
    const parsed = JSON.parse(raw) as Partial<CalendarPrefs>;
    const out: CalendarPrefs = { ...DEFAULT_CAL_PREFS, ...parsed };
    if (out.weekStart !== 0 && out.weekStart !== 1 && out.weekStart !== 6) out.weekStart = 1;
    if (out.mode !== 'gregorian' && out.mode !== 'both' && out.mode !== 'hijri') out.mode = 'both';
    return out;
  } catch { return { ...DEFAULT_CAL_PREFS }; }
}

function saveCalPrefs(p: CalendarPrefs): void {
  try { localStorage.setItem(CAL_PREFS_LS_KEY, JSON.stringify(p)); }
  catch { /* quota */ }
}

function injectStyles(): void {
  const w = window as unknown as Record<string, unknown>;
  if (w[STYLE_FLAG]) return;
  w[STYLE_FLAG] = true;
  const style = document.createElement('style');
  style.textContent = `
    .yawmi-shell {
      max-width: 720px;
      margin: 0 auto;
      padding: 16px 12px 80px;
      color: var(--ln-text-primary, #0A0A0A);
    }
    .yawmi-cal-header {
      display: flex; align-items: center; justify-content: space-between;
      gap: 12px; margin: 4px 0 16px 0;
    }
    .yawmi-cal-title { font-size: 1.25rem; font-weight: 700; line-height: 1.2; }
    .yawmi-cal-subtitle {
      font-size: 0.85rem; color: var(--ln-text-secondary, #555);
      margin-top: 2px;
    }
    .yawmi-cal-nav {
      display: flex; gap: 8px; align-items: center;
    }
    .yawmi-cal-btn {
      width: 40px; height: 40px;
      border-radius: var(--ln-radius-md, 12px);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.08));
      background: var(--ln-bg-card, #fff);
      color: var(--ln-text-primary, #0A0A0A);
      font-size: 1rem; font-weight: 600; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .yawmi-cal-btn:hover {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-today-btn {
      width: auto; padding: 0 16px; font-size: 0.85rem;
    }
    .yawmi-cal-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .yawmi-cal-dow {
      text-align: center;
      font-size: 0.72rem;
      font-weight: 700;
      color: var(--ln-text-secondary, #555);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 4px 0 8px 0;
    }
    .yawmi-cal-cell {
      position: relative;
      min-height: 64px;
      padding: 6px 6px 4px 6px;
      border-radius: 10px;
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.06));
      cursor: pointer;
      text-align: start;
      font: inherit;
      color: inherit;
      display: flex; flex-direction: column;
      transition: transform var(--motion-duration-instant) var(--motion-easing-default), box-shadow var(--motion-duration-instant) var(--motion-easing-default);
    }
    .yawmi-cal-cell:hover {
      box-shadow: var(--ln-shadow-sm, 0 1px 4px rgba(0,0,0,0.08));
      transform: translateY(-1px);
    }
    .yawmi-cal-cell:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    .yawmi-cal-cell--other-month {
      opacity: 0.35;
    }
    .yawmi-cal-cell--today {
      background: var(--ln-bg-attention, #FFF8E7);
      border-color: var(--ln-accent-ceremonial, #C8A96A);
      border-width: 2px;
    }
    .yawmi-cal-cell--has-events {
      border-color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-cell--selected {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
    }
    .yawmi-cal-day-num {
      font-size: 1rem; font-weight: 700; line-height: 1;
    }
    .yawmi-cal-hijri-num {
      font-size: 0.7rem; color: var(--ln-text-secondary, #777);
      margin-top: 2px;
    }
    .yawmi-cal-cell--selected .yawmi-cal-hijri-num {
      color: var(--ln-accent-text, #fff); opacity: 0.85;
    }
    .yawmi-cal-events-row {
      margin-top: auto;
      display: flex; gap: 2px; flex-wrap: wrap;
      font-size: 0.7rem; line-height: 1;
    }
    .yawmi-cal-event-dot {
      display: inline-block;
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-event-dot--islamic { background: var(--ln-accent-ceremonial, #C8A96A); }
    .yawmi-cal-event-dot--national { background: var(--ln-accent, #0A0A0A); }
    /* Phase 3c — personal-event dot (green to distinguish from religious + national). */
    .yawmi-cal-event-dot--personal { background: #16A34A; }
    /* Phase 3c — personal event list item (flex row with delete button). */
    .yawmi-cal-event-item--personal { display: flex; align-items: flex-start; gap: 10px; }
    .yawmi-cal-event-del {
      flex-shrink: 0;
      width: 26px; height: 26px;
      display: inline-flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: transparent;
      border: 1px solid var(--ln-border, rgba(10, 10, 10, 0.12));
      color: var(--ln-text-secondary, #64748B);
      font: inherit; font-size: 14px; font-weight: 700; line-height: 1;
      cursor: pointer;
      padding: 0;
      transition: background var(--motion-duration-fast) var(--motion-easing-default),
                  color var(--motion-duration-fast) var(--motion-easing-default);
    }
    .yawmi-cal-event-del:hover {
      background: rgba(220, 38, 38, 0.10);
      color: #DC2626;
      border-color: #DC2626;
    }
    /* Phase 3c — "Add personal event" CTA in the day-detail panel. */
    .yawmi-cal-add-btn {
      margin-top: 12px;
      display: inline-flex; align-items: center; gap: 6px;
      padding: 10px 14px;
      background: var(--ln-bg-card, #FFFFFF);
      border: 1px dashed var(--ln-border, rgba(10, 10, 10, 0.18));
      border-radius: 12px;
      font: inherit;
      font-size: 13px; font-weight: 600;
      color: var(--ln-text-primary, #1E293B);
      cursor: pointer;
      transition: background var(--motion-duration-fast) var(--motion-easing-default),
                  border-color var(--motion-duration-fast) var(--motion-easing-default);
    }
    .yawmi-cal-add-btn:hover {
      background: var(--ln-bg-secondary, rgba(10, 10, 10, 0.04));
      border-color: var(--ln-accent, #0A0A0A);
    }
    /* Phase 3c/3d — add-event + settings panels (replaces CalendarModal forms). */
    .yawmi-cal-form {
      display: flex; flex-direction: column; gap: 14px;
      padding: 18px 6px 80px 6px;
    }
    .yawmi-cal-form-label {
      font-size: 12px; font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: var(--ln-text-secondary, #64748B);
    }
    .yawmi-cal-form-input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid var(--ln-border, rgba(10, 10, 10, 0.12));
      border-radius: 10px;
      background: var(--ln-bg-card, #FFFFFF);
      color: var(--ln-text-primary, #1E293B);
      font: inherit;
      font-size: 14px;
      outline: none;
    }
    .yawmi-cal-form-input:focus {
      border-color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-form-textarea {
      resize: vertical;
      min-height: 80px;
    }
    .yawmi-cal-form-hint {
      font-size: 11px;
      color: var(--ln-text-secondary, #64748B);
      line-height: 1.4;
    }
    .yawmi-cal-form-save {
      margin-top: 6px;
      padding: 12px 16px;
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #FFFFFF);
      border: none; border-radius: 12px;
      font: inherit; font-size: 15px; font-weight: 700;
      cursor: pointer;
      min-height: 48px;
    }
    .yawmi-cal-form-save:active { transform: scale(0.98); }
    .yawmi-cal-segmented {
      display: flex; gap: 4px;
      padding: 4px;
      background: var(--ln-bg-secondary, rgba(10, 10, 10, 0.04));
      border-radius: 12px;
    }
    .yawmi-cal-seg {
      flex: 1;
      padding: 8px 10px;
      background: transparent; border: none;
      border-radius: 8px;
      font: inherit; font-size: 13px; font-weight: 600;
      color: var(--ln-text-primary, #1E293B);
      cursor: pointer;
      transition: background var(--motion-duration-fast) var(--motion-easing-default);
    }
    .yawmi-cal-seg.is-on {
      background: var(--ln-bg-card, #FFFFFF);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
    .yawmi-cal-toggle {
      display: flex; align-items: center; gap: 10px;
      font-size: 14px; font-weight: 500;
      cursor: pointer;
    }
    .yawmi-cal-toggle input {
      width: 18px; height: 18px;
      cursor: pointer;
    }
    /* ST2 Pattern D — alternate-date dot is hollow ring (less likely than primary). */
    .yawmi-cal-event-dot--alt {
      background: transparent;
      border: 1.5px solid currentColor;
      color: var(--ln-accent-ceremonial, #C8A96A);
    }
    /* ST2 Pattern D — alternate-date cell badge: small "?" in upper corner. */
    .yawmi-cal-cell--alternate {
      border-style: dashed;
      border-color: var(--ln-accent-ceremonial, #C8A96A);
    }
    .yawmi-cal-cell__alt-badge {
      position: absolute;
      top: 2px;
      inset-inline-end: 4px;
      width: 14px; height: 14px;
      line-height: 14px; text-align: center;
      font-size: 10px; font-weight: 800;
      border-radius: 50%;
      background: var(--ln-accent-ceremonial, #C8A96A);
      color: #fff;
      pointer-events: none;
    }
    /* Phase B H2 — Yawm al-Shakk per-cell badge (day 30 of moon-sighting
       sensitive months). Sits ABOVE the ST2 alt-badge in z-order; the two
       almost never overlap because YalS triggers on Hijri day 30 while
       alt-badge triggers on Gregorian alt-date — but if they do, YalS wins
       because "this day's identity is uncertain" is more fundamental than
       "this is the day-before fallback". */
    .yawmi-cal-cell__hesitation-badge {
      position: absolute;
      top: 2px;
      inset-inline-end: 4px;
      width: 14px; height: 14px;
      line-height: 14px; text-align: center;
      font-size: 10px; font-weight: 800;
      border-radius: 50%;
      background: var(--ln-accent-warning, var(--ln-accent-ceremonial, #C8A96A));
      color: #fff;
      pointer-events: none;
      z-index: 2;
    }
    /* Location/authority indicator below the calendar subtitle. Surfaces
       WHICH moonsighting body the displayed Hijri dates are calibrated for
       — critical for trust because authorities can differ by 1–2 days on
       Eid / Ramadan boundaries. Same data + same look as the (now
       redirected) CalendarModal indicator; shared via shell/LocationSource. */
    .yawmi-cal-ref {
      display: inline-flex; align-items: center; gap: 4px;
      margin-top: 4px;
      font-size: 11px; line-height: 1.3;
      color: var(--ln-text-secondary, #64748B);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 100%;
    }
    .yawmi-cal-ref-flag { font-size: 12px; }
    .yawmi-cal-ref-city { font-weight: 600; color: var(--ln-text-primary, #1E293B); }
    .yawmi-cal-ref-sep { opacity: 0.4; margin: 0 1px; }
    .yawmi-cal-ref-auth { opacity: 0.85; }
    .yawmi-cal-ref-prompt {
      background: rgba(10, 10, 10, 0.06);
      border: 1px solid rgba(10, 10, 10, 0.18);
      border-radius: 999px;
      padding: 4px 10px;
      cursor: pointer;
      font: inherit;
      color: var(--ln-accent, #0A0A0A);
      transition: background var(--motion-duration-fast) var(--motion-easing-default), transform var(--motion-duration-fast) var(--motion-easing-default);
    }
    .yawmi-cal-ref-prompt:hover { background: rgba(10, 10, 10, 0.10); }
    .yawmi-cal-ref-prompt:active { transform: scale(0.98); }
    .yawmi-cal-ref-prompt .yawmi-cal-ref-auth {
      font-weight: 600;
      opacity: 1;
      color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-ref-prompt .yawmi-cal-ref-cta {
      margin-inline-start: 4px;
      opacity: 0.7;
    }
    /* ST2 Pattern D — explainer in the day-detail panel. */
    .yawmi-cal-alt-explainer {
      font-size: 0.82rem;
      color: var(--ln-text-secondary, #555);
      background: var(--ln-bg-attention, #FFF8E7);
      padding: 8px 10px;
      border-radius: 8px;
      margin: 6px 0 10px 0;
      line-height: 1.45;
    }
    .yawmi-cal-event-emoji { font-size: 0.85rem; }
    .yawmi-cal-day-detail {
      margin: 16px 0 0 0;
      padding: 14px 16px;
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.08));
      border-radius: var(--ln-radius-lg, 16px);
    }
    .yawmi-cal-day-detail h3 {
      margin: 0 0 4px 0; font-size: 1.05rem; font-weight: 700;
    }
    .yawmi-cal-day-detail .subtitle {
      font-size: 0.85rem; color: var(--ln-text-secondary, #555);
      margin: 0 0 10px 0;
    }
    .yawmi-cal-event-list {
      list-style: none; padding: 0; margin: 0;
      display: flex; flex-direction: column; gap: 8px;
    }
    .yawmi-cal-event-item {
      display: flex; gap: 10px; align-items: flex-start;
      padding: 8px 10px; border-radius: 10px;
      background: var(--ln-bg-page, #fafafa);
    }
    .yawmi-cal-event-item__icon {
      flex: 0 0 auto; font-size: 1.1rem;
    }
    .yawmi-cal-event-item__label {
      font-weight: 600; font-size: 0.92rem;
    }
    .yawmi-cal-event-item__sub {
      font-size: 0.78rem; color: var(--ln-text-secondary, #666);
      margin-top: 2px;
    }
    .yawmi-cal-empty {
      padding: 24px;
      text-align: center;
      color: var(--ln-text-secondary, #666);
      font-size: 0.92rem;
    }
    [dir="rtl"] .yawmi-cal-cell { text-align: end; }
  `;
  document.head.appendChild(style);
}

interface CalendarState {
  /** First day of the displayed month (always day 1 in local TZ). */
  cursor: Date;
  /** ISO date the user clicked (or null = today by default). */
  selectedIso: string | null;
  /** Phase 3c — true while the "add personal event" form is open. */
  addOpen: boolean;
  /** Phase 3d — true while the settings panel is open. */
  settingsOpen: boolean;
}

export class YawmiModule implements PreviewableAweniModule {
  readonly id = 'yawmi';
  readonly routes = ROUTES;
  private container: HTMLElement | null = null;
  private currentMountId: string | null = null;
  private state: CalendarState | null = null;
  private activeTab: 'calendar' | 'habits' | 'alarms' | 'reminders' | 'duas' | 'health' | 'people' | 'history' = 'calendar';
  private habitTab: HabitTab | null = null;
  private alarmTab: AlarmTab | null = null;
  private reminderTab: ReminderTab | null = null;
  private duaTab: DuaJournalTab | null = null;
  private healthTab: HealthTab | null = null;
  private peopleTab: PeopleTab | null = null;
  private historyTab: HistoryTab | null = null;
  private contentEl: HTMLElement | null = null;
  private chipListener: ((e: Event) => void) | null = null;
  /** SourceAttributionStrip mounted in the calendar header. Recreated on
   *  each render() (cheap; lets month-nav reflect any location change). */
  private attrStrip: SourceAttributionStrip | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.currentMountId = `yawmi_${++mountSeq}`;
    injectStyles();

    const hash = location.hash;
    this.activeTab = hash.includes('/yawmi/habits') ? 'habits'
      : hash.includes('/yawmi/alarms') ? 'alarms'
      : hash.includes('/yawmi/reminders') ? 'reminders'
      : hash.includes('/yawmi/duas') ? 'duas'
      : hash.includes('/yawmi/health') ? 'health'
      : hash.includes('/yawmi/people') ? 'people'
      : hash.includes('/yawmi/history') ? 'history'
      : 'calendar';

    // Phase A H1 backbone (2026-05-15): warm the Hijri month cache so every
    // calendar cell reads the country-authoritative date. Fire-and-forget,
    // does not block mount. Country code from prefs/Location falls back to
    // 'MA' per AWENI default reference (see HijriService.refreshHijriMonth).
    const _h1Today = new Date();
    void import('../../services/HijriService').then(m =>
      m.refreshHijriMonth(_h1Today.getFullYear(), _h1Today.getMonth() + 1)
    );

    // Honor `#/yawmi/calendar?date=YYYY-MM-DD` deep-links (used by the
    // soft-redirect from the Home hero date chip + CalendarTimePair). When
    // the query param is present and valid, the calendar opens on that
    // date's month with that day selected. Anything malformed falls back
    // to today silently.
    const today = new Date();
    let initialDate = today;
    const qIdx = hash.indexOf('?');
    if (qIdx >= 0) {
      try {
        const params = new URLSearchParams(hash.slice(qIdx + 1));
        const dateParam = params.get('date');
        if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
          const [y, m, d] = dateParam.split('-').map(Number);
          const parsed = new Date(y, m - 1, d);
          if (!isNaN(parsed.getTime())) initialDate = parsed;
        }
      } catch { /* malformed query — silently fall back to today */ }
    }
    this.state = {
      cursor: new Date(initialDate.getFullYear(), initialDate.getMonth(), 1),
      selectedIso: this.toIso(initialDate),
      addOpen: false,
      settingsOpen: false,
    };
    this.renderShell(ctx);

    // Listen for chip-row tab switches dispatched by src/shell/ChipRow.ts
    // (canonical tab nav per Motionov 3-surface design).
    this.chipListener = (e: Event) => {
      const detail = (e as CustomEvent<{ tab: string }>).detail;
      if (!detail?.tab || detail.tab === this.activeTab) return;
      this.habitTab?.unmount(); this.habitTab = null;
      this.alarmTab?.unmount(); this.alarmTab = null;
      this.reminderTab?.unmount(); this.reminderTab = null;
      this.duaTab?.unmount(); this.duaTab = null;
      this.healthTab?.unmount(); this.healthTab = null;
      this.peopleTab?.unmount(); this.peopleTab = null;
      this.historyTab?.unmount(); this.historyTab = null;
      this.activeTab = detail.tab as 'calendar' | 'habits' | 'alarms' | 'reminders' | 'duas' | 'health' | 'people' | 'history';
      this.mountTab(ctx);
    };
    window.addEventListener('aweni:yawmi:tab', this.chipListener);
  }

  unmount(): void {
    if (this.chipListener) {
      window.removeEventListener('aweni:yawmi:tab', this.chipListener);
      this.chipListener = null;
    }
    this.attrStrip?.unmount(); this.attrStrip = null;
    this.habitTab?.unmount(); this.habitTab = null;
    this.alarmTab?.unmount(); this.alarmTab = null;
    this.reminderTab?.unmount(); this.reminderTab = null;
    this.duaTab?.unmount(); this.duaTab = null;
    this.healthTab?.unmount(); this.healthTab = null;
    this.peopleTab?.unmount(); this.peopleTab = null;
    this.historyTab?.unmount(); this.historyTab = null;
    if (this.container) this.container.innerHTML = '';
    this.container = null;
    this.contentEl = null;
    this.currentMountId = null;
    this.state = null;
  }

  private renderShell(ctx: AweniContext): void {
    if (!this.container) return;
    // Tabs are rendered by the shell-level ChipRow (src/shell/ChipRow.ts) per
    // Motionov 3-surface design — no per-module duplicate tab nav here. Tab
    // switching arrives via the `aweni:yawmi:tab` window event which the chip
    // row dispatches when a chip is tapped (see ChipRow.ts onClick handler).
    this.container.innerHTML = `
      <div style="max-width:720px;margin:0 auto;padding:0 12px 80px">
        <div id="yw-content"></div>
      </div>`;
    this.contentEl = this.container.querySelector('#yw-content');
    this.mountTab(ctx);
  }

  private mountTab(ctx: AweniContext): void {
    if (!this.contentEl) return;
    this.contentEl.innerHTML = '';
    switch (this.activeTab) {
      case 'calendar': this.render(ctx); break;
      case 'habits': this.habitTab = new HabitTab(this.contentEl); this.habitTab.mount(ctx); break;
      case 'alarms': this.alarmTab = new AlarmTab(this.contentEl); this.alarmTab.mount(ctx); break;
      case 'reminders': this.reminderTab = new ReminderTab(this.contentEl); this.reminderTab.mount(ctx); break;
      case 'duas': this.duaTab = new DuaJournalTab(this.contentEl); this.duaTab.mount(ctx); break;
      case 'health': this.healthTab = new HealthTab(this.contentEl); this.healthTab.mount(ctx); break;
      case 'people': this.peopleTab = new PeopleTab(this.contentEl); this.peopleTab.mount(ctx); break;
      case 'history': this.historyTab = new HistoryTab(this.contentEl); this.historyTab.mount(ctx); break;
    }
  }

  destroy(_instance: MountedInstance): void {
    this.unmount();
  }

  // -------------------------------------------------------------------------
  // Calendar render — synchronous, idempotent.
  // -------------------------------------------------------------------------
  private render(ctx: AweniContext): void {
    const el = this.contentEl ?? this.container;
    if (!el || !this.state) return;

    // Phase 3c/3d — modal-style panels render OVER the grid. Toggle flags
    // win against the default grid render.
    if (this.state.settingsOpen) { this.renderSettingsPanel(ctx); return; }
    if (this.state.addOpen) { this.renderAddEventPanel(ctx); return; }

    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';
    const locale = ctx.i18n.locale;
    // Phase 3d — pick prefs once per render. Drives weekStart + mode + layers.
    const prefs = loadCalPrefs();

    const cursor = this.state.cursor;
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthLabel = new Intl.DateTimeFormat(locale, {
      month: 'long', year: 'numeric',
    }).format(cursor);

    // Hijri reference subtitle. Single source of truth across surfaces:
    //   - When the cursor month contains TODAY → show today's Hijri (matches
    //     the AWENI Home hero date chip, so users never see two values for
    //     the same moment).
    //   - Otherwise → show the 1st-of-month Hijri (matches CalendarModal's
    //     legacy convention so the Yawmi calendar is a drop-in replacement).
    // Avoid `midMonth` (the 15th): it was stale on day-15 rollover and could
    // disagree with the Home hero by ±1 day.
    const _today = new Date();
    const _cursorHoldsToday =
      _today.getFullYear() === year && _today.getMonth() === month;
    const hijriRef = _cursorHoldsToday ? _today : new Date(year, month, 1);
    const hijri = gregorianToHijri(hijriRef);
    const hijriLabel = formatHijri(hijri, locale);

    // Phase 3d — week-start preference (Mon/Sat/Sun). Default Mon (ISO 8601).
    // Aligns the grid's leftmost column with the user's chosen first-day.
    const ws = prefs.weekStart;
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = (firstOfMonth.getDay() - ws + 7) % 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    const lastOfMonth = new Date(year, month + 1, 0);
    const endOffset = (6 - ((lastOfMonth.getDay() - ws + 7) % 7) + 7) % 7;
    const gridEnd = new Date(year, month + 1, endOffset);

    // Fetch all events in the visible range (Islamic + national + cultural).
    // Phase 3d — honor prefs.countryCode override + layer toggles.
    const country = (prefs.countryCode as Country) ?? readUserCountry();
    const cal = createHijriCalendar({
      country,
      method: 'sighting_local' as HijriMethod,
      hijriOffset: 0,
      locale,
      includeNationalHolidays: prefs.showHolidays && country !== 'global',
      includeAstronomical: false,
    });
    const range = cal.getEventsInRange(gridStart, gridEnd);
    // Phase 3c — also fetch personal events from EventsService (the same
    // storage the deprecated CalendarModal wrote to, so existing entries
    // are preserved). Filtered to current grid range + layer toggle.
    const personalEvents = prefs.showPersonal
      ? getEventsService().listPersonalEvents().filter((e) => {
          return e.date >= this.toIso(gridStart) && e.date <= this.toIso(gridEnd);
        })
      : [];

    // Bucket events by ISO date for fast cell lookup.
    // ST2 Pattern D — moon-sighting events appear on BOTH primary and
    // alternate dates. Per-bucket flag `isAlternate` lets the cell renderer
    // mark the less-likely day with a `?` badge so users see the uncertainty
    // at a glance without losing the badge entirely on either date.
    interface CellEvent {
      ev: ResolvedEvent;
      isAlternate: boolean;
    }
    const eventsByDate: Record<string, CellEvent[]> = {};
    // Phase 3d — apply Islamic / National layer toggles. createHijriCalendar
    // already filters National (showHolidays flag); here we filter Islamic.
    for (const ev of range.events) {
      if (ev.source.kind === 'islamic_holiday' && !prefs.showIslamic) continue;
      (eventsByDate[ev.primaryDate] ||= []).push({ ev, isAlternate: false });
      if (ev.alternateDate && ev.alternateDate !== ev.primaryDate) {
        (eventsByDate[ev.alternateDate] ||= []).push({ ev, isAlternate: true });
      }
    }
    // Phase 3c — bucket personal events for fast cell lookup. Separate bucket
    // from `eventsByDate` so we can render a distinct green dot + a delete
    // button in renderDayDetail.
    const personalByDate: Record<string, CalendarEvent[]> = {};
    for (const pe of personalEvents) {
      (personalByDate[pe.date] ||= []).push(pe);
    }

    const todayIso = this.toIso(new Date());

    // Day-of-week headers — Phase 3d honors prefs.weekStart so Mon/Sat/Sun
    // user choice aligns with the grid (was hardcoded Sun-first).
    const dowLabels: string[] = [];
    const sunday = new Date(2024, 0, 7); // 2024-01-07 = Sunday
    const dowFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + ((ws + i) % 7));
      dowLabels.push(dowFmt.format(d));
    }

    // Build grid cells.
    const cells: string[] = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) {
      const iso = this.toIso(cur);
      const inMonth = cur.getMonth() === month;
      const isToday = iso === todayIso;
      const isSelected = iso === this.state.selectedIso;
      const cellEvents = eventsByDate[iso] || [];
      const hijriDay = gregorianToHijri(cur).day;

      // ST2 Pattern D: when ALL events on this cell are alternate-date
      // moon-sighting events (i.e. the user's country leans toward the
      // primary date but this day is the ±1 fallback), show the `?` badge
      // and mute the dot opacity so the eye lands on the more-likely day.
      const hasAnyEvent = cellEvents.length > 0;
      const allAlternate = hasAnyEvent && cellEvents.every((c) => c.isAlternate);
      const anyMoonSighting = cellEvents.some(
        (c) => c.ev.source.uncertaintyKind === 'moon_sighting'
      );
      const showAlternateBadge = anyMoonSighting && allAlternate;

      const classes = [
        'yawmi-cal-cell',
        !inMonth ? 'yawmi-cal-cell--other-month' : '',
        isToday ? 'yawmi-cal-cell--today' : '',
        isSelected && !isToday ? 'yawmi-cal-cell--selected' : '',
        hasAnyEvent ? 'yawmi-cal-cell--has-events' : '',
        showAlternateBadge ? 'yawmi-cal-cell--alternate' : '',
      ].filter(Boolean).join(' ');

      // Phase 3c — include personal-event dot (green) alongside Islamic + national.
      const personalDots = (personalByDate[iso] || []).slice(0, 1).map(() =>
        `<span class="yawmi-cal-event-dot yawmi-cal-event-dot--personal" aria-hidden="true"></span>`,
      );
      const themedDots = cellEvents.slice(0, 3 - personalDots.length).map((c) => {
        const cls = c.ev.source.kind === 'islamic_holiday'
          ? 'yawmi-cal-event-dot--islamic'
          : 'yawmi-cal-event-dot--national';
        const altCls = c.isAlternate ? ' yawmi-cal-event-dot--alt' : '';
        return `<span class="yawmi-cal-event-dot ${cls}${altCls}" aria-hidden="true"></span>`;
      });
      const dotsHtml = [...themedDots, ...personalDots].join('');

      // Pluck the underlying ResolvedEvent[] for aria + listeners.
      const resolvedEvents = cellEvents.map((c) => c.ev);

      // Phase B H2 — Yawm al-Shakk hesitation overlay (day 30 of moon-sighting
      // sensitive Hijri months — Sha'bān → Ramadan, Ramaḍān → Eid al-Fitr,
      // Dhū al-Qaʿdah → Dhū al-Ḥijjah/Hajj, Dhū al-Ḥijjah → Hijri new year).
      // Per all 4 Sunni schools (Bukhari 1909, Tirmidhi 686, al-Nawawi):
      // day 30 is THE doubtful day. Rendered ONLY within ±14 days of today.
      // Takes priority over the ST2 alt-badge on the same slot because
      // "this day's identity is uncertain" is more fundamental than "this
      // is the day-before fallback for an event". Both convey moon-sighting
      // semantics, so showing one is sufficient.
      const cellHijri = gregorianToHijri(cur);
      const hesitation = getHesitationContext(cellHijri, cur);
      const hesitationBadge = hesitation.applies && hesitation.tooltipKey
        ? `<span class="yawmi-cal-cell__hesitation-badge"
                  aria-label="${escHtml(t(hesitation.tooltipKey) || 'Day of hesitation — depends on tonight’s moon sighting')}"
                  title="${escHtml(t(hesitation.tooltipKey) || 'Day of hesitation — depends on tonight’s moon sighting')}">?</span>`
        : '';
      const altBadge = !hesitation.applies && showAlternateBadge
        ? `<span class="yawmi-cal-cell__alt-badge"
                  aria-label="${escHtml(t('yawmi_cal_alt_aria') || 'Alternate moon-sighting date — primary is the previous day')}">?</span>`
        : '';

      // Phase 3d — honor mode pref: 'gregorian' hides Hijri num; 'hijri' hides
      // Gregorian num; 'both' (default) shows both.
      const showGreg = prefs.mode !== 'hijri';
      const showHijri = prefs.mode !== 'gregorian';
      cells.push(`
        <button type="button" class="${classes}"
                data-iso="${escHtml(iso)}"
                aria-label="${escHtml(this.cellAria(cur, resolvedEvents, locale, t, allAlternate))}">
          ${hesitationBadge}${altBadge}
          ${showGreg ? `<span class="yawmi-cal-day-num">${cur.getDate()}</span>` : ''}
          ${showHijri ? `<span class="yawmi-cal-hijri-num">${hijriDay}</span>` : ''}
          <span class="yawmi-cal-events-row">${dotsHtml}</span>
        </button>
      `);
      cur.setDate(cur.getDate() + 1);
    }

    // Detail panel for the selected day.
    const selectedDate = this.state.selectedIso
      ? new Date(this.state.selectedIso + 'T12:00:00Z')
      : new Date();
    const selectedCellEvents = this.state.selectedIso
      ? (eventsByDate[this.state.selectedIso] || [])
      : [];
    const selectedEvents = selectedCellEvents.map((c) => c.ev);
    const selectedAllAlternate = selectedCellEvents.length > 0
      && selectedCellEvents.every((c) => c.isAlternate);
    const selectedLabel = new Intl.DateTimeFormat(locale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(selectedDate);
    const selectedHijri = formatHijri(gregorianToHijri(selectedDate), locale);

    // Phase 3c — pass personal events for the selected day so the detail
    // panel can render them with a delete button + "Add event" CTA.
    const selectedPersonal = this.state.selectedIso
      ? (personalByDate[this.state.selectedIso] || [])
      : [];
    const detailHtml = this.renderDayDetail(
      selectedLabel, selectedHijri, selectedEvents, locale, t, selectedAllAlternate, selectedPersonal
    );

    // Phase 2 (2026-05-16) — SourceAttributionStrip replaces the inline
    // location pill. Same data (flag + city + authority), now expandable to
    // surface per-feature source rows ("Hijri authority", "Public holidays")
    // with in-app info popovers. Mounted into `[data-source-attribution]`
    // after innerHTML below. No external links — keeps users inside AWENI's
    // Learning Castle per user directive 2026-05-16 + Searchov-validated
    // <button aria-expanded> + <dl> pattern.

    el.innerHTML = `
      <div class="yawmi-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="yawmi-cal-header">
          <div>
            <div class="yawmi-cal-title">${escHtml(monthLabel)}</div>
            <div class="yawmi-cal-subtitle">${escHtml(hijriLabel)}</div>
            <div data-source-attribution></div>
          </div>
          <nav class="yawmi-cal-nav" aria-label="${escHtml(t('yawmi_cal_nav_aria') || 'Calendar navigation')}">
            <button type="button" class="yawmi-cal-btn" data-action="prev"
                    aria-label="${escHtml(t('yawmi_cal_prev') || 'Previous month')}">‹</button>
            <button type="button" class="yawmi-cal-btn yawmi-cal-today-btn" data-action="today">
              ${escHtml(t('yawmi_cal_today') || 'Today')}
            </button>
            <button type="button" class="yawmi-cal-btn" data-action="next"
                    aria-label="${escHtml(t('yawmi_cal_next') || 'Next month')}">›</button>
            <button type="button" class="yawmi-cal-btn" data-action="settings"
                    aria-label="${escHtml(t('calendar_settings') || 'Settings')}" title="${escHtml(t('calendar_settings') || 'Settings')}">⚙</button>
          </nav>
        </header>
        <ol class="yawmi-cal-grid" role="grid">
          ${dowLabels.map((d) => `<li class="yawmi-cal-dow" role="columnheader">${escHtml(d)}</li>`).join('')}
          ${cells.join('')}
        </ol>
        ${detailHtml}
      </div>
    `;

    // Wire up nav + cell click handlers.
    el.querySelectorAll<HTMLElement>('.yawmi-cal-nav button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-action');
        if (!this.state) return;
        if (action === 'prev') {
          this.state.cursor = new Date(year, month - 1, 1);
        } else if (action === 'next') {
          this.state.cursor = new Date(year, month + 1, 1);
        } else if (action === 'today') {
          const now = new Date();
          this.state.cursor = new Date(now.getFullYear(), now.getMonth(), 1);
          this.state.selectedIso = this.toIso(now);
        } else if (action === 'settings') {
          this.state.settingsOpen = true;
        }
        this.render(ctx);
      });
    });
    el.querySelectorAll<HTMLElement>('.yawmi-cal-cell').forEach((cell) => {
      cell.addEventListener('click', () => {
        const iso = cell.getAttribute('data-iso');
        if (!iso || !this.state) return;
        this.state.selectedIso = iso;
        this.render(ctx);
      });
    });
    // Phase 3c — "Add personal event" button in the day-detail panel.
    el.querySelector<HTMLElement>('[data-action="open-add-event"]')?.addEventListener('click', () => {
      if (!this.state) return;
      this.state.addOpen = true;
      this.render(ctx);
    });
    // Phase 3c — delete personal event from the day-detail list.
    el.querySelectorAll<HTMLElement>('[data-personal-del]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = btn.getAttribute('data-personal-del');
        if (!id) return;
        getEventsService().removePersonalEvent(id);
        this.render(ctx);
      });
    });

    // Phase 2 — mount the SourceAttributionStrip into the header placeholder.
    // Stripped of prior mount on re-render (cheap; lets the strip pick up
    // any LocationService change between month-nav clicks). The strip owns
    // its own `data-action="set-location"` handler internally, dispatching
    // `aweni:request-location-priming` — no duplicate handler needed here.
    this.attrStrip?.unmount();
    const attrSlot = el.querySelector<HTMLElement>('[data-source-attribution]');
    if (attrSlot) {
      // Only pass the Public-holidays row when a country is set (otherwise
      // the row's value is unusable). The strip auto-prepends the Hijri
      // authority row when LocationLabel.auth is present.
      let raw: string | null = null;
      try { raw = localStorage.getItem('aweni_location'); } catch { /* */ }
      const hasCountry = !!(raw && (() => {
        try { return !!(JSON.parse(raw!) as { countryCode?: string }).countryCode; }
        catch { return false; }
      })());
      const extraRows = hasCountry
        ? [{
            id: 'holidays',
            label: t('attribution_holidays_label') || 'Public holidays',
            value: t('attribution_holidays_value') || 'Government holiday database',
            info: t('attribution_holidays_info') || 'Public-holiday dates are loaded from the Nager.Date open database, refreshed every 7 days.',
          }]
        : [];
      this.attrStrip = new SourceAttributionStrip({
        id: 'yawmi-cal',
        rows: extraRows,
        strings: {
          setCity: t('calendar_hijri_ref_prompt') || 'Set your city for accurate Hijri date',
          setCityTooltip: t('calendar_hijri_ref_prompt_tooltip') || 'Hijri dates differ between countries. Set your city to use the local authority.',
          expandAria: t('attribution_expand') || 'Show sources used on this page',
          collapseAria: t('attribution_collapse') || 'Hide sources',
          infoAria: t('attribution_info') || 'More about this source',
          hijriAuthorityLabel: t('attribution_hijri_authority') || 'Hijri authority',
        },
      });
      this.attrStrip.mount(attrSlot);
    }
  }

  private renderDayDetail(
    label: string,
    hijriLabel: string,
    events: ResolvedEvent[],
    locale: string,
    t: (k: string) => string,
    allAlternate: boolean,
    /** Phase 3c — personal events for this day, rendered with delete buttons. */
    personal: CalendarEvent[] = [],
  ): string {
    const items = events.map((ev) => {
      const isIslamic = ev.source.kind === 'islamic_holiday';
      const icon = isIslamic ? '🕌' : '📅';
      const i18nName = isIslamic
        ? (t(`holiday_${ev.source.id}`) || ev.source.nameI18n[locale as 'en'] || ev.source.id)
        : (ev.source.nameI18n[locale as 'en'] || ev.source.id);
      const sub: string[] = [];
      if (ev.showAsRange && ev.alternateDate && ev.alternateDate !== ev.primaryDate) {
        sub.push(`${t('holiday_uncertainty_badge_pm1') || '±1 day'}`);
      }
      if (ev.source.isPublicHoliday) {
        sub.push(t('yawmi_cal_public_holiday') || 'Public holiday');
      }
      return `
        <li class="yawmi-cal-event-item">
          <span class="yawmi-cal-event-item__icon" aria-hidden="true">${icon}</span>
          <div>
            <div class="yawmi-cal-event-item__label">${escHtml(i18nName)}</div>
            ${sub.length ? `<div class="yawmi-cal-event-item__sub">${escHtml(sub.join(' · '))}</div>` : ''}
          </div>
        </li>
      `;
    }).join('');
    // Phase 3c — personal events with delete buttons. Distinct icon + delete X.
    const personalItems = personal.map((pe) => `
      <li class="yawmi-cal-event-item yawmi-cal-event-item--personal">
        <span class="yawmi-cal-event-item__icon" aria-hidden="true">📌</span>
        <div style="flex:1;min-width:0;">
          <div class="yawmi-cal-event-item__label">${escHtml(pe.title)}</div>
          ${pe.description ? `<div class="yawmi-cal-event-item__sub">${escHtml(pe.description)}</div>` : ''}
        </div>
        <button type="button" class="yawmi-cal-event-del" data-personal-del="${escHtml(pe.id)}"
                aria-label="${escHtml(t('common_delete') || 'Delete')}" title="${escHtml(t('common_delete') || 'Delete')}">×</button>
      </li>
    `).join('');
    // ST2 Pattern D explainer: when this day is ALL alternate-dates of
    // moon-sighting events, surface the uncertainty model in plain words
    // so users don't think the calendar is inconsistent.
    const altExplainer = allAlternate
      ? `<p class="yawmi-cal-alt-explainer">${escHtml(t('yawmi_cal_alt_explainer') || 'These events depend on local moon-sighting. Your country typically follows the previous day; this is the alternate ±1 day.')}</p>`
      : '';
    const emptyMsg = events.length === 0 && personal.length === 0
      ? `<p class="yawmi-cal-empty">${escHtml(t('yawmi_cal_no_events') || 'No events on this day.')}</p>`
      : '';
    // Phase 3c — Add personal event CTA always visible (replaces prior empty-state-only path).
    const addBtn = `
      <button type="button" class="yawmi-cal-add-btn" data-action="open-add-event"
              aria-label="${escHtml(t('calendar_add_event') || 'Add personal event')}">
        + ${escHtml(t('calendar_add_event') || 'Add personal event')}
      </button>`;
    return `
      <section class="yawmi-cal-day-detail">
        <h3>${escHtml(label)}</h3>
        <p class="subtitle">${escHtml(hijriLabel)}</p>
        ${altExplainer}
        ${emptyMsg}
        ${items || personalItems ? `<ul class="yawmi-cal-event-list">${items}${personalItems}</ul>` : ''}
        ${addBtn}
      </section>
    `;
  }

  // -------------------------------------------------------------------------
  // Phase 3c — "Add personal event" panel (replaces the deprecated
  // CalendarModal.renderAddEvent flow). Persists via EventsService so the
  // entry appears as a green dot in the grid + a line in the day-detail.
  // -------------------------------------------------------------------------
  private renderAddEventPanel(ctx: AweniContext): void {
    const el = this.contentEl ?? this.container;
    if (!el || !this.state) return;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';
    const dateIso = this.state.selectedIso ?? this.toIso(new Date());
    el.innerHTML = `
      <div class="yawmi-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="yawmi-cal-header">
          <button type="button" class="yawmi-cal-btn" data-action="back"
                  aria-label="${escHtml(t('common_back') || 'Back')}">‹</button>
          <div style="flex:1;min-width:0;">
            <div class="yawmi-cal-title">${escHtml(t('calendar_add_event') || 'Add personal event')}</div>
            <div class="yawmi-cal-subtitle">${escHtml(dateIso)}</div>
          </div>
        </header>
        <div class="yawmi-cal-form">
          <label class="yawmi-cal-form-label" for="yw-evt-title">${escHtml(t('calendar_event_title') || 'Title')}</label>
          <input id="yw-evt-title" type="text" maxlength="120" class="yawmi-cal-form-input"
                 placeholder="${escHtml(t('calendar_event_title_placeholder') || 'e.g., Dentist appointment')}" autocomplete="off">
          <label class="yawmi-cal-form-label" for="yw-evt-desc">${escHtml(t('calendar_event_desc') || 'Description (optional)')}</label>
          <textarea id="yw-evt-desc" maxlength="500" rows="3" class="yawmi-cal-form-input yawmi-cal-form-textarea"
                    placeholder="${escHtml(t('calendar_event_desc_placeholder') || 'Add details…')}"></textarea>
          <button type="button" class="yawmi-cal-form-save" data-action="save">
            ${escHtml(t('calendar_save_event') || 'Save event')}
          </button>
        </div>
      </div>
    `;
    el.querySelector<HTMLElement>('[data-action="back"]')?.addEventListener('click', () => {
      if (!this.state) return;
      this.state.addOpen = false;
      this.render(ctx);
    });
    el.querySelector<HTMLElement>('[data-action="save"]')?.addEventListener('click', () => {
      if (!this.state) return;
      const title = el.querySelector<HTMLInputElement>('#yw-evt-title')?.value || '';
      const desc = el.querySelector<HTMLTextAreaElement>('#yw-evt-desc')?.value || '';
      if (title.trim().length === 0) return;
      getEventsService().addPersonalEvent(dateIso, title, desc);
      this.state.addOpen = false;
      this.render(ctx);
    });
    setTimeout(() => el.querySelector<HTMLInputElement>('#yw-evt-title')?.focus(), 50);
  }

  // -------------------------------------------------------------------------
  // Phase 3d — Settings panel (mode + week-start + country + layer toggles).
  // Same prefs schema as the deprecated CalendarModal (localStorage key
  // `aweni_calendar_prefs`) so migration is zero-cost.
  // -------------------------------------------------------------------------
  private renderSettingsPanel(ctx: AweniContext): void {
    const el = this.contentEl ?? this.container;
    if (!el || !this.state) return;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';
    const prefs = loadCalPrefs();
    const seg = (name: string, value: string, label: string, on: boolean): string =>
      `<button type="button" class="yawmi-cal-seg ${on ? 'is-on' : ''}"
               data-seg="${name}" data-value="${value}"
               role="radio" aria-checked="${on}">${escHtml(label)}</button>`;
    el.innerHTML = `
      <div class="yawmi-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="yawmi-cal-header">
          <button type="button" class="yawmi-cal-btn" data-action="back"
                  aria-label="${escHtml(t('common_back') || 'Back')}">‹</button>
          <div style="flex:1;min-width:0;">
            <div class="yawmi-cal-title">${escHtml(t('calendar_settings_title') || 'Calendar settings')}</div>
          </div>
        </header>
        <div class="yawmi-cal-form">
          <label class="yawmi-cal-form-label">${escHtml(t('calendar_setting_mode') || 'Display mode')}</label>
          <div class="yawmi-cal-segmented" role="radiogroup">
            ${seg('mode', 'gregorian', t('calendar_mode_gregorian') || 'Gregorian', prefs.mode === 'gregorian')}
            ${seg('mode', 'both', t('calendar_mode_both') || 'Both', prefs.mode === 'both')}
            ${seg('mode', 'hijri', t('calendar_mode_hijri') || 'Hijri', prefs.mode === 'hijri')}
          </div>
          <label class="yawmi-cal-form-label">${escHtml(t('calendar_setting_week_start') || 'Week starts on')}</label>
          <div class="yawmi-cal-segmented" role="radiogroup">
            ${seg('weekStart', '1', t('calendar_day_mon') || 'Mon', prefs.weekStart === 1)}
            ${seg('weekStart', '6', t('calendar_day_sat') || 'Sat', prefs.weekStart === 6)}
            ${seg('weekStart', '0', t('calendar_day_sun') || 'Sun', prefs.weekStart === 0)}
          </div>
          <label class="yawmi-cal-form-label">${escHtml(t('calendar_setting_layers') || 'Layers')}</label>
          <label class="yawmi-cal-toggle">
            <input type="checkbox" data-toggle="showHolidays" ${prefs.showHolidays ? 'checked' : ''}>
            <span>${escHtml(t('calendar_layer_holidays') || 'Public holidays')}</span>
          </label>
          <label class="yawmi-cal-toggle">
            <input type="checkbox" data-toggle="showIslamic" ${prefs.showIslamic ? 'checked' : ''}>
            <span>${escHtml(t('calendar_layer_islamic') || 'Islamic events')}</span>
          </label>
          <label class="yawmi-cal-toggle">
            <input type="checkbox" data-toggle="showPersonal" ${prefs.showPersonal ? 'checked' : ''}>
            <span>${escHtml(t('calendar_layer_personal') || 'Personal events')}</span>
          </label>
          <label class="yawmi-cal-form-label" for="yw-cal-cc">${escHtml(t('calendar_setting_country') || 'Country override (2 letters)')}</label>
          <input id="yw-cal-cc" type="text" maxlength="2" class="yawmi-cal-form-input"
                 value="${escHtml(prefs.countryCode ?? '')}" placeholder="MA"
                 aria-describedby="yw-cal-cc-hint">
          <div id="yw-cal-cc-hint" class="yawmi-cal-form-hint">${escHtml(t('calendar_setting_country_hint') || 'Overrides the auto-detected country for Hijri authority and public-holiday list.')}</div>
          <button type="button" class="yawmi-cal-form-save" data-action="save">
            ${escHtml(t('calendar_save') || 'Save')}
          </button>
        </div>
      </div>
    `;
    el.querySelector<HTMLElement>('[data-action="back"]')?.addEventListener('click', () => {
      if (!this.state) return;
      this.state.settingsOpen = false;
      this.render(ctx);
    });
    el.querySelectorAll<HTMLElement>('[data-seg]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('data-seg');
        const value = btn.getAttribute('data-value') ?? '';
        const cur = loadCalPrefs();
        if (name === 'mode' && (value === 'gregorian' || value === 'both' || value === 'hijri')) {
          cur.mode = value;
        } else if (name === 'weekStart') {
          const n = parseInt(value, 10);
          if (n === 0 || n === 1 || n === 6) cur.weekStart = n;
        }
        saveCalPrefs(cur);
        this.render(ctx);
      });
    });
    el.querySelectorAll<HTMLElement>('[data-toggle]').forEach((cb) => {
      cb.addEventListener('change', () => {
        const key = cb.getAttribute('data-toggle');
        const checked = (cb as HTMLInputElement).checked;
        const cur = loadCalPrefs();
        if (key === 'showHolidays' || key === 'showIslamic' || key === 'showPersonal') {
          cur[key] = checked;
        }
        saveCalPrefs(cur);
      });
    });
    el.querySelector<HTMLElement>('[data-action="save"]')?.addEventListener('click', () => {
      if (!this.state) return;
      const inp = el.querySelector<HTMLInputElement>('#yw-cal-cc');
      const cc = (inp?.value || '').trim().toUpperCase().slice(0, 2);
      const cur = loadCalPrefs();
      cur.countryCode = /^[A-Z]{2}$/.test(cc) ? cc : null;
      saveCalPrefs(cur);
      this.state.settingsOpen = false;
      // Refresh authoritative Hijri month for the new country.
      void import('../../services/HijriService').then((m) => {
        const c = this.state?.cursor ?? new Date();
        m.refreshHijriMonth(c.getFullYear(), c.getMonth() + 1, cur.countryCode || undefined);
      });
      this.render(ctx);
    });
  }

  private cellAria(
    d: Date,
    events: ResolvedEvent[],
    locale: string,
    t: (k: string) => string,
    allAlternate: boolean,
  ): string {
    const dateLabel = new Intl.DateTimeFormat(locale, {
      weekday: 'long', day: 'numeric', month: 'long',
    }).format(d);
    if (events.length === 0) return dateLabel;
    const eventLabel = events.length === 1
      ? events[0].source.nameI18n[locale as 'en'] || events[0].source.id
      : `${events.length} ${t('yawmi_cal_events_short') || 'events'}`;
    // ST2 Pattern D — narrate the ±1 uncertainty for screen readers.
    const altSuffix = allAlternate
      ? ` (${t('yawmi_cal_alt_aria_suffix') || 'alternate moon-sighting date — primary is the previous day'})`
      : '';
    return `${dateLabel} — ${eventLabel}${altSuffix}`;
  }

  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // -------------------------------------------------------------------------
  // preview() — used by the AWENI home Today zone. Reports today's "next
  // event" and any upcoming holidays. Stays cheap (<5ms).
  // -------------------------------------------------------------------------
  async preview(ctx: AweniContext): Promise<PreviewSnapshot> {
    try {
      const t = ctx.i18n.t.bind(ctx.i18n);
      const country = readUserCountry();
      const cal = createHijriCalendar({
        country,
        method: 'sighting_local' as HijriMethod,
        hijriOffset: 0,
        locale: ctx.i18n.locale,
        includeNationalHolidays: false,
        includeAstronomical: false,
      });
      const next = cal.getNextHoliday();

      let primaryLabel = t('yawmi_preview_today_label') || 'Today';
      let primaryValue = t('yawmi_preview_placeholder_value') || 'Daily check';
      let secondaryValue: string | undefined;
      let bgVariant: PreviewSnapshot['bgVariant'] = 'neutral';

      if (next) {
        const name = t(`holiday_${next.holiday.id}`) || next.holiday.name[ctx.i18n.locale] || next.holiday.id;
        if (next.daysAway === 0) {
          primaryLabel = name;
          primaryValue = t('holiday_today') || 'Today';
          bgVariant = 'success';
        } else if (next.daysAway === 1) {
          primaryLabel = name;
          primaryValue = t('holiday_tomorrow') || 'Tomorrow';
          bgVariant = 'attention';
        } else if (next.daysAway <= 30) {
          primaryLabel = name;
          primaryValue = `${t('holiday_in') || 'in'} ${next.daysAway} ${t('holiday_days') || 'days'}`;
          bgVariant = next.daysAway <= 7 ? 'attention' : 'neutral';
        } else {
          primaryLabel = t('yawmi_preview_today_label') || 'Today';
          primaryValue = t('yawmi_preview_placeholder_value') || 'Daily check';
          secondaryValue = `${name} ${t('holiday_in') || 'in'} ${next.daysAway} ${t('holiday_days') || 'days'}`;
        }
      }

      const snapshot: PreviewSnapshot = {
        primaryMetric: { label: primaryLabel, value: primaryValue },
        secondaryMetric: secondaryValue ? { label: '', value: secondaryValue } : undefined,
        ctaLabel: t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: `Yawmi — ${primaryLabel} ${primaryValue}`,
        bgVariant,
        computedAt: new Date().toISOString(),
      };
      try { localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot)); }
      catch { /* ignore */ }
      return snapshot;
    } catch {
      return {
        primaryMetric: { label: '', value: '' },
        ctaLabel: ctx.i18n.t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: 'Yawmi',
        errorState: {
          message: ctx.i18n.t('preview_tap_to_open') || 'Tap to open',
          recoveryRoute: '#/yawmi',
        },
        computedAt: new Date().toISOString(),
      };
    }
  }
}

export default new YawmiModule();
